import type { ApplicationContext } from './shared-types'

import { onEachPageTeardown } from '@app/adapters/page-teardown'
import type {
  DataAlterationGuardRequest,
  DataAlterationGuardResult,
} from '@app/shared/services/data-alteration-guard.types'

// The contract with silverfin-solid: the SDK owns the requestId, and it reports
// a committed write so a later write in the same visit needs no modal.
export const GUARD_REQUEST_EVENT = 'solid:data-alteration-guard-request'
export const GUARD_RESULT_EVENT = 'solid:data-alteration-guard-result'
export const COMMITTED_EVENT = 'solid:data-alteration-committed'

// Only a request that came from the SDK gets its result mirrored back to
// `document`. Without this the bridge would echo every verdict the monolith's
// own callers ask for.
const pendingSdkRequests = new Set<string>()

export default (appContext: ApplicationContext): ApplicationContext => {
  // A request the SDK abandons would otherwise sit here for the rest of the
  // document's life, and a verdict for it could still be mirrored onto a later
  // page.
  onEachPageTeardown(() => pendingSdkRequests.clear())

  document.addEventListener(GUARD_REQUEST_EVENT, (event: Event) => {
    const request = (event as CustomEvent<DataAlterationGuardRequest>).detail

    pendingSdkRequests.add(request.requestId)
    window.eventHub.$emit('data-alteration-guard-request', request)
  })

  // skipUnprepare travels with the write, so the refetched status keeps the
  // preparation the server kept.
  document.addEventListener(COMMITTED_EVENT, (event: Event) => {
    const { skipUnprepare } = (
      event as CustomEvent<{ skipUnprepare?: boolean }>
    ).detail

    window.eventHub.$emit('reloadWorkflowReviewStatusData', { skipUnprepare })
  })

  window.eventHub.$on(
    'data-alteration-guard-result',
    (result: DataAlterationGuardResult) => {
      if (!pendingSdkRequests.delete(result.requestId)) return

      document.dispatchEvent(
        new CustomEvent<DataAlterationGuardResult>(GUARD_RESULT_EVENT, {
          detail: result,
        })
      )
    }
  )

  return appContext
}
